import { likeApi } from "@/config/httpClient"
import { ContentLikes } from "@/generated/openapi"
import { LikeEvent } from "@/generated/openapi"
import { LikeCategory } from '@/models/LikeCategory'
import { LikesCacheForBundle } from "@/models/LikesCacheForBundle";
import { ToggleLikeResponse } from "@/models/ToggleLikeResponse";

class LikeService {

  // Cache for myLikes
  private myLikes: Promise<LikeEvent[]> | null = null;
  private bundledLikesCache: LikesCacheForBundle[] = [];

  private getMyLikes(): Promise<LikeEvent[]> {
    if(!this.myLikes){
      this.myLikes = likeApi.myLikes().then((serverResponse) => serverResponse.data).catch(() => []);
    }
    return this.myLikes;
  }


  public getLikes(category: LikeCategory, contentId: string, bundleId?:string): Promise<ContentLikes> {

     if(bundleId === undefined) {
      return likeApi.likesByContent(contentId, category)
      .then(resp => resp.data)
    } else {

      return this.getLikesForBundle(bundleId).bundle.then(bl => {
        if (bl.bundleLikes !== undefined) {
          for(let i = 0; i < bl.bundleLikes.length; i++) {
            if (bl.bundleLikes[i].contentId === contentId) {
              return bl.bundleLikes[i]
            }
          }
        }
        return {} as ContentLikes
      })
    }
  }

  private getLikesForBundle(bundleId: string):LikesCacheForBundle {
    for(let i = 0; i < this.bundledLikesCache.length; i++) {
      const elem = this.bundledLikesCache[i]
      if(elem.bundleId === bundleId) {
        return elem
      }
    }

    const tmpBundle:LikesCacheForBundle = {
      bundleId: bundleId,
      bundle: likeApi.likesByBundle(bundleId).then(axiosResponse => {
        return axiosResponse.data
      }).catch(() => {return {}})
    }
    this.bundledLikesCache.push(tmpBundle)
    return tmpBundle
  }

  private removeFromCacheIfCached(bundleId: string): void {
    for(let i = 0; i < this.bundledLikesCache.length; i++) {
      const x = this.bundledLikesCache[i]
      if (x.bundleId === bundleId) {
        this.bundledLikesCache.splice(i, 1)
        return
      }
    }
  }

  public toggleLike(category: LikeCategory, contentId: string, bundleId?:string): Promise<ToggleLikeResponse> {
    // If you want to refresh "myLikes" set the parameter to true.
    return this.isLikedByMe(contentId, bundleId,  true)
      .then(likedByMe => {
        if (likedByMe) {
          return likeApi.unlike(contentId, category).then(() => false).catch(() => true);
        } else {
          return likeApi.like(contentId, category, bundleId).then(() => true).catch(() => false);
        }
      })
      .then(async (newLikedByMe) => {
        return this.getMyLikes().then((myLikes) => {
          if (newLikedByMe) {
            // If we called "like", we add a new element to our list
            this.addPersonalLikeEvent(myLikes, category, contentId, bundleId);
          } else {
            // if we called "unlike", we remove an element from our cachingList.
            this.removePersonalLikeEvent(myLikes, contentId);
          }
          if (bundleId !== undefined) {
            this.removeFromCacheIfCached(bundleId)
          }
          const tlr : ToggleLikeResponse = {
            likedByMe: newLikedByMe,
            numberOfLikes: 0
          };
          return tlr;
        })
      })
      .then((tlr) => {
        return this.getLikes(category, contentId, bundleId).then(cls => {
            tlr.numberOfLikes = (cls.likes != undefined) ? cls.likes : 0;
            return tlr;
        })
      })
   }

  public isLikedByMe(contentId: string, bundleId?:string, forceReload ?: boolean): Promise<boolean> {
    if (forceReload !== undefined && forceReload) {
      this.myLikes = likeApi.myLikes().then((serverResponse) => serverResponse.data).catch(() => []);
    }
    return this.getMyLikes().then(likeEvents => {
      return likeEvents.some((searchElement) => {
        if (bundleId === undefined) {
          return (searchElement.contentid === contentId)
        } else {
          return (searchElement.bundleid === bundleId) && (searchElement.contentid === contentId)
        }
      });
    }).catch(() => {
      return false;
    });
  }

  /**
   * Add a LikeEvent to the local personal LikeEvents cache.
   *
   * @param likeEvents
   * @param category
   * @param contentId
   * @param bundleId
   */
  private addPersonalLikeEvent(likeEvents: LikeEvent[], category: LikeCategory, contentId: string, bundleId?:string) {
    const newLikeEvent: LikeEvent = {
      bundleid: bundleId,
      category: category,
      contentid: contentId,
    }
    likeEvents.push(newLikeEvent);
  }

  /**
   * Remove LikeEvent from local personal LikeEvents cache
   *
   * @param likeEvents
   * @param contentId
   * @param bundleId
   */
  private removePersonalLikeEvent(likeEvents: LikeEvent[],contentId: string, bundleId?:string) {
    const elemToRemove = likeEvents.find((searchElement) => {
      if (bundleId === undefined) {
        return (searchElement.contentid === contentId)
      } else {
        return (searchElement.bundleid === bundleId && searchElement.contentid === contentId)
      }
    })
    if(elemToRemove !== undefined) {
      likeEvents.splice(likeEvents.indexOf(elemToRemove), 1);
    }
  }

}

const likeService = new LikeService()
export default likeService
